/**
 * SchemaGroupCard Component
 *
 * Card displaying a Schema Group with its stats and action buttons.
 * Shows schema count, organization assignments, and quick actions.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { SchemaGroup } from '@/services/api/groupApi';
import { FileText, Building2, Pencil, Trash2 } from 'lucide-react';

interface SchemaGroupCardProps {
  group: SchemaGroup;
  schemaCount?: number;
  onEdit: () => void;
  onDelete: () => void;
  onManageSchemas: () => void;
  onAssignOrganizations: () => void;
  canManage?: boolean;
}

export const SchemaGroupCard: React.FC<SchemaGroupCardProps> = ({
  group,
  schemaCount = 0,
  onEdit,
  onDelete,
  onManageSchemas,
  onAssignOrganizations,
  canManage = true,
}) => {
  const assignedOrgCount = group.assignedOrganizations?.length || 0;

  return (
    <div className="border border-border rounded-lg p-4 bg-background hover:bg-accent/30 transition-colors">
      <div className="flex items-start justify-between">
        {/* Group Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium truncate">{group.name}</h4>
            <span className="flex-shrink-0 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
              {group.slug}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {group.description || 'No description'}
          </p>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              {schemaCount} schema{schemaCount !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              <Building2 className="h-4 w-4" />
              {assignedOrgCount} org{assignedOrgCount !== 1 ? 's' : ''} assigned
            </span>
          </div>
        </div>

        {/* Actions */}
        {canManage && (
          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onManageSchemas}
              title="Manage schemas"
            >
              Schemas
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onAssignOrganizations}
              title="Assign organizations"
            >
              Orgs
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label="Edit group"
              title="Edit group"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label="Delete group"
              title="Delete group"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemaGroupCard;
